import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { interval } from 'rxjs';
import { map, mergeMap, take, takeWhile } from 'rxjs/operators';
import { ApiErrorFactory } from '../../core/api-errors';
import { StringCookie } from '../../core/cookie';
import { Job } from '../../core/job';
import { Site, SiteService } from '../../core/site';
import { PublishTemplateResponse, TemplateApiService } from '../../core/template';
import { ConfirmationDialog, SuccessDialog } from '../dialog';
import { FeatureFlagService } from '../../core/feature-flag.service';

@Component({
  selector: 'app-manage-template-card',
  templateUrl: './publish-template-card.component.html',
  styleUrls: ['./publish-template-card.component.scss'],
  standalone: false,
})
export class PublishTemplateComponent implements OnInit {
  @Input() siteId: string;
  @Input() site: Site;
  public disableButton = false;
  private jobStatus: string;
  private jobIdCookie: StringCookie;
  private templateSubtitle: string;
  private templateCTA: string;
  private templateConfirmation: string;
  private templateSuccessTitle: string;
  private templateSuccessSubtitle: string;
  private showToggleFeatureFlagId = 'wsp_managing_distributed_templates';
  public isTemplateDistributed = true;
  public toggleVisible = false;
  public distributeTemplateLabel = 'Distribute published template';

  private readonly dialog = inject(MatDialog);
  private readonly alertService = inject(SnackbarService);
  private readonly templateService = inject(TemplateApiService);
  private readonly siteService = inject(SiteService);
  private readonly translate = inject(TranslateService);
  private readonly apiErrorFactory = inject(ApiErrorFactory);
  private readonly snowplow = inject(ProductAnalyticsService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.jobIdCookie = new StringCookie(`publishTemplateJobId:${this.siteId}`);
    this.templateManagementText();
    const jobId = this.jobIdCookie.get();
    if (jobId) {
      this.jobHasStarted(jobId);
    }
    this.featureFlagService
      .isFeatureEnabled(this.site.partnerId, this.showToggleFeatureFlagId, '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res) {
          this.toggleVisible = true;
          if (this.site.template) {
            this.loadTemplateDistributionStatus();
          }
        }
      });
  }

  private loadTemplateDistributionStatus(): void {
    this.templateService
      .getIsTemplateDistributed(this.siteId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (isDistributed: boolean) => {
          this.isTemplateDistributed = isDistributed;
        },
        (err) => {
          const parsedError = this.apiErrorFactory.parseErrorResponse(err);
          this.alertService.openErrorSnack(
            parsedError.getUserMessage(),
            this.translate.instant('ALERTS.GENERAL.DISMISS'),
          );
        },
      );
  }

  onTemplateDistributionToggle(): void {
    const newValue = !this.isTemplateDistributed;
    this.templateService
      .setTemplateAsDistributed(this.siteId, newValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (success: boolean) => {
          if (success) {
            this.isTemplateDistributed = newValue;
          }
        },
        (err) => {
          const parsedError = this.apiErrorFactory.parseErrorResponse(err);
          this.alertService.openErrorSnack(
            parsedError.getUserMessage(),
            this.translate.instant('ALERTS.GENERAL.DISMISS'),
          );
        },
      );
  }

  showDialog(): void {
    this.disableButton = true;
    this.dialog
      .open(ConfirmationDialog, {
        data: {
          content: this.templateConfirmation,
          action: this.templateCTA,
        },
        disableClose: false,
        hasBackdrop: true,
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((shouldPublish: boolean) => {
        if (shouldPublish) {
          this.templateService
            .publishTemplate(this.siteId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
              (response: PublishTemplateResponse) => {
                this.jobHasStarted(response.jobId);
              },
              (err) => {
                const parsedError = this.apiErrorFactory.parseErrorResponse(err);
                this.jobIsDone(parsedError.getUserMessage());
              },
            );
        } else {
          this.disableButton = false;
          this.jobIdCookie.clear();
        }
      });
  }

  protected pollForJob(jobId: string): void {
    interval(3000)
      .pipe(
        mergeMap(() => this.siteService.getJobForSite(this.siteId, jobId)),
        map((job: Job) => {
          this.jobStatus = job.status;
          return job.status;
        }),
        takeWhile((jobStatus) => jobStatus === Job.IN_PROGRESS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: (err) => {
          const parsedError = this.apiErrorFactory.parseErrorResponse(err);
          this.jobIsDone(parsedError.getUserMessage());
        },
        complete: () => {
          const errMessage: string =
            this.jobStatus === Job.FAILED
              ? this.translate.instant('PAGES.OVERVIEW.MANAGE_TEMPLATE_CARD.GENERAL_PUBLISH_ERROR')
              : undefined;
          this.jobIsDone(errMessage);
        },
      });
  }

  private jobHasStarted(jobId: string): void {
    this.jobIdCookie.set(jobId);
    this.pollForJob(jobId);
    this.disableButton = true;
  }

  private jobIsDone(errMessage?: string): void {
    if (errMessage) {
      this.alertService.openErrorSnack(errMessage, this.translate.instant('ALERTS.GENERAL.DISMISS'));
    } else {
      this.dialog.open(SuccessDialog, {
        data: {
          content: this.templateSuccessTitle,
          emoji: '&#127881',
          subtitle: this.templateSuccessSubtitle,
          action: 'BUTTONS.CLOSE',
        },
      });
    }
    this.disableButton = false;
    this.jobIdCookie.clear();
  }

  private templateManagementText(): void {
    this.siteService.site$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((s) => {
      if (s.templateVersion === 0) {
        this.templateSubtitle = 'PAGES.OVERVIEW.MANAGE_TEMPLATE_CARD.NEW.SUBTITLE';
        this.templateCTA = 'PAGES.OVERVIEW.MANAGE_TEMPLATE_CARD.NEW.PUBLISH_BUTTON';
        this.templateConfirmation = 'PAGES.OVERVIEW.MANAGE_TEMPLATE_CARD.NEW.CONFIRMATION_DESCRIPTION';
        this.templateSuccessTitle = 'PAGES.OVERVIEW.MANAGE_TEMPLATE_CARD.NEW.SUCCESS_TITLE';
        this.templateSuccessSubtitle = 'PAGES.OVERVIEW.MANAGE_TEMPLATE_CARD.NEW.SUCCESS_SUBTITLE';
      } else {
        this.templateSubtitle = 'PAGES.OVERVIEW.MANAGE_TEMPLATE_CARD.EXISTING.SUBTITLE';
        this.templateCTA = 'PAGES.OVERVIEW.MANAGE_TEMPLATE_CARD.EXISTING.PUBLISH_BUTTON';
        this.templateConfirmation = 'PAGES.OVERVIEW.MANAGE_TEMPLATE_CARD.EXISTING.CONFIRMATION_DESCRIPTION';
        this.templateSuccessTitle = 'PAGES.OVERVIEW.MANAGE_TEMPLATE_CARD.EXISTING.SUCCESS_TITLE';
        this.templateSuccessSubtitle = 'PAGES.OVERVIEW.MANAGE_TEMPLATE_CARD.EXISTING.SUCCESS_SUBTITLE';
      }
    });
  }
}
